import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";
// EMPTY FORM
const EMPTY_FORM = {
  faq_title: "",
  faq_desc: "",
};

// MANAGE FAQ
export default function ManageFaq() {
  // DATA
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEARCH
  const [search, setSearch] = useState("");

  // PAGINATION
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // FORM
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // MODALS
  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // FETCH DATA
  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/faqs", {
        params: {
          page,
          limit,
          search,
        },
      });

      const responseData = response.data;

      // BACKWARD COMPATIBILITY
      if (Array.isArray(responseData)) {
        setData(responseData);
        setTotal(responseData.length);
        setTotalPages(Math.ceil(responseData.length / limit));
      } else {
        const rows = responseData?.data || [];
        const totalCount = Number(responseData?.total || 0);

        setData(rows);
        setTotal(totalCount);

        setTotalPages(
          Number(responseData?.totalPages || Math.ceil(totalCount / limit))
        );
      }
    } catch (error) {
      console.error("FETCH FAQ ERROR:", error.response?.data || error.message);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load FAQs"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  // LOAD DATA
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SEARCH
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // RESET FORM
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // OPEN ADD MODAL
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // CLOSE FORM MODAL
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // HANDLE FORM CHANGE
  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // EDIT
  const startEdit = (row) => {
    setForm({
      faq_title: row.faq_title || "",
      faq_desc: row.faq_desc || "",
    });

    setEditId(row.faq_id);
    setFormModal(true);
  };

  // VIEW
  const handleView = (row) => {
    setViewModal(row);
  };

  // SUBMIT ADD OR EDIT
  const handleSubmit = async () => {
    // VALIDATION
    if (!form.faq_title.trim()) {
      toasts.error("FAQ Question is required");
      return;
    }

    if (!form.faq_desc.trim()) {
      toasts.error("FAQ Answer is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        faq_title: form.faq_title.trim(),
        faq_desc: form.faq_desc.trim(),
      };

      // UPDATE
      if (editId) {
        await API.put(`/faqs/${editId}`, payload);

        toasts.success("FAQ updated successfully");
      } else {
        // ADD
        await API.post("/faqs", payload);

        toasts.success("FAQ added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData();
    } catch (error) {
      console.error("FAQ submit error:", error.response?.data || error.message);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // DELETE
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/faqs/${delModal.faq_id}`);

      toasts.success("FAQ deleted successfully");

      setDelModal(null);

      // LAST RECORD ON PAGE
      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete FAQ error:", error.response?.data || error.message);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // RETURN
  return (
    <Layout activePage="FAQ">
      <main className="dashboard-main">
        {/* TABLE CARD */}
        <div className="table-card">
          {/* HEADER */}
          <div className="table-card-head">
            {/* TITLE */}
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage FAQ</span>

              <span className="rec-count ms-1">{total} items</span>
            </h3>

            {/* SEARCH */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search ..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* ADD */}
            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add FAQ
            </button>
          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">FAQ Question</th>
                  <th className="um-th text-center">FAQ Answer</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {/* LOADING */}
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 4 }).map((_, cellIndex) => (
                        <td key={cellIndex} className="um-td text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  // EMPTY
                  <tr>
                    <td
                      colSpan={4}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiPackage />
                      </div>

                      <div className="empty-title">No FAQ records found</div>

                      <div className="empty-sub">
                        Use the Add FAQ button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  // DATA
                  data.map((row, index) => (
                    <tr
                      key={row.faq_id}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      {/* NUMBER */}
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      {/* QUESTION */}
                      <td className="um-td">
                        <div
                          style={{
                            maxWidth: "350px",
                            margin: "0 auto",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.faq_title || ""}
                        >
                          <strong>{row.faq_title || "—"}</strong>
                        </div>
                      </td>

                      {/* ANSWER */}
                      <td className="um-td">
                        <div
                          style={{
                            maxWidth: "450px",
                            margin: "0 auto",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.faq_desc || ""}
                        >
                          {row.faq_desc || "—"}
                        </div>
                      </td>

                      {/* ACTION */}
                      <td className="um-td text-center">
                        <div
                          className="action-buttons"
                          style={{
                            display: "flex",
                            gap: "3px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {/* VIEW */}
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          {/* EDIT */}
                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          {/* DELETE */}
                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* PAGINATION */}
            {totalPages > 0 && (
              <div className="pagination">
                <span className="pagin-info">
                  {total > 0
                    ? `Showing ${(page - 1) * limit + 1} – ${Math.min(
                        page * limit,
                        total
                      )} of ${total}`
                    : ""}
                </span>

                <div className="d-flex align-items-center gap-2">
                  {/* PREVIOUS */}
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page === 1 || loading}
                    onClick={() =>
                      setPage((previous) => Math.max(1, previous - 1))
                    }
                  >
                    ← Prev
                  </button>

                  {/* PAGE */}
                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  {/* NEXT */}
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page >= totalPages || loading}
                    onClick={() =>
                      setPage((previous) => Math.min(totalPages, previous + 1))
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ADD / EDIT MODAL */}
        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? <FiEdit size={24} /> : <FiPlus size={24} />}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId ? "Edit FAQ" : "Add FAQ"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Manage frequently asked questions
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}
              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* QUESTION */}
                  <div className="col-md-12">
                    <label className="field-label">FAQ Question *</label>

                    <input
                      type="text"
                      className="rd-input"
                      style={{
                        textAlign: "start",
                      }}
                      placeholder="Enter FAQ question"
                      maxLength={255}
                      value={form.faq_title}
                      onChange={(e) =>
                        handleChange("faq_title", e.target.value)
                      }
                    />
                  </div>

                  {/* ANSWER */}
                  <div className="col-md-12">
                    <label className="field-label">FAQ Answer *</label>

                    <textarea
                      className="rd-input"
                      style={{
                        textAlign: "start",
                        resize: "vertical",
                      }}
                      rows="6"
                      placeholder="Enter FAQ answer"
                      value={form.faq_desc}
                      onChange={(e) => handleChange("faq_desc", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update FAQ
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add FAQ
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VIEW MODAL */}
        {viewModal && (
          <div className="um-overlay" onClick={() => setViewModal(null)}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> FAQ Details
                  </h2>

                  <p className="form-card-sub mb-0">View FAQ information</p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}
              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* QUESTION */}
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      FAQ Question
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        {viewModal.faq_title || "—"}
                      </div>
                    </div>
                  </div>

                  {/* ANSWER */}
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-2">
                      FAQ Answer
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div
                        className="fw-semibold"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                        }}
                      >
                        {viewModal.faq_desc || "No answer available."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit FAQ
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {delModal && (
          <div
            className="um-overlay"
            onClick={() => {
              if (!delSaving) {
                setDelModal(null);
              }
            }}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* ICON */}
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                {/* TITLE */}
                <h2 className="delete-title">Delete FAQ?</h2>

                {/* QUESTION */}
                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.faq_title}
                  </span>
                </div>

                {/* DESCRIPTION */}
                <p className="delete-description">
                  This FAQ will be permanently deleted.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                {/* ACTIONS */}
                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        
      </main>
      
    </Layout>
  );
}
